import React, { ChangeEvent, useMemo } from 'react';
import { ColumnDef, Row } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import SearchBox from 'components/common/SearchBox';
import FilterTab from 'components/common/FilterTab';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { QAProcessorCall } from 'services/qaprocessor.service';
import { format } from 'date-fns';
import Badge from 'components/base/Badge';

interface TableProps {
  calls: QAProcessorCall[];
}

export const QAProcessorLogTable: React.FC<TableProps> = ({ calls }) => {
  const formatDateTime = (date: Date | string | undefined) => {
    if (!date) return '-';
    return format(new Date(date), 'MMM dd, yyyy - HH:mm:ss');
  };

  const columns: ColumnDef<QAProcessorCall>[] = [
    {
      accessorKey: 'createdat',
      header: 'Created',
      cell: ({ row: { original } }) => {
        const date = new Date(original.createdat);
        return <span>{format(date, 'MMM dd, yyyy - HH:mm:ss')}</span>;
      },
      meta: {
        headerProps: { style: { width: '8%' }, className: 'pe-2 ps-0 fs-10' },
        cellProps: { className: 'py-0 pe-2 ps-0 fs-10 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'callid',
      header: 'Call ID',
      cell: ({ row: { original } }) => original.callid,
      meta: {
        headerProps: { style: { width: '9%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 fs-10 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row: { original } }) => {
        const getBadgeColor = (status: string) => {
          switch (status) {
            case 'completed':
              return 'success';
            case 'error':
              return 'danger';
            case 'transcribing':
              return 'info';
            case 'summarizing':
              return 'warning';
            case 'analyzing':
              return 'primary';
            default:
              return 'secondary';
          }
        };

        return (
          <Badge variant="phoenix" bg={getBadgeColor(original.status)}>
            {original.status}
          </Badge>
        );
      },
      // eslint-disable-next-line
      filterFn: (row: Row<QAProcessorCall>, columnId: string, filterValue: string) => {
        if (!filterValue || filterValue === 'all') return true;
        return row.getValue(columnId) === filterValue;
      },
      meta: {
        headerProps: { style: { width: '6%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap' }
      }
    },
    {
      id: 'transcription',
      header: 'Transcription',
      cell: ({ row: { original } }) => (
        <div className="d-flex flex-column">
          <small className="text-muted">
            Start: {formatDateTime(original.transcriptionstarttime)}
          </small>
          <small className="text-muted">
            End: {formatDateTime(original.transcriptionendtime)}
          </small>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap' }
      }
    },
    {
      id: 'summarization',
      header: 'Summary',
      cell: ({ row: { original } }) => (
        <div className="d-flex flex-column">
          <small className="text-muted">
            Start: {formatDateTime(original.summarizationstarttime)}
          </small>
          <small className="text-muted">
            End: {formatDateTime(original.summarizationendtime)}
          </small>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap' }
      }
    },
    {
      id: 'analysis',
      header: 'Analysis',
      cell: ({ row: { original } }) => (
        <div className="d-flex flex-column">
          <small className="text-muted">
            Start: {formatDateTime(original.analysisstarttime)}
          </small>
          <small className="text-muted">
            End: {formatDateTime(original.analysisendtime)}
          </small>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'retrycount',
      header: 'Retries',
      cell: ({ row: { original } }) => original.retrycount,
      meta: {
        headerProps: { style: { width: '4%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 fs-10 text-center' }
      }
    },
    {
      accessorKey: 'errormessage',
      header: 'Error Message',
      cell: ({ row: { original } }) => (
        <span className="text-danger">{original.errormessage || ''}</span>
      ),
      meta: {
        headerProps: { style: { width: '40%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 fs-10 white-space-nowrap' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: calls,
    columns,
    pageSize: 14,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleFilterItemClick = useMemo(
    () => (columnId: string, value: string) => {
      const column = table.getColumn(columnId);
      column?.setFilterValue(value === 'all' ? '' : value);
    },
    [table]
  );

  const tabItems = useMemo(() => {
    const getStatusCount = (status: string) =>
      calls.filter(call => call.status === status).length;

    return [
      {
        label: 'All',
        value: 'all',
        onClick: () => handleFilterItemClick('status', 'all'),
        count: calls.length
      },
      {
        label: 'Transcribing',
        value: 'transcribing',
        onClick: () => handleFilterItemClick('status', 'transcribing'),
        count: getStatusCount('transcribing')
      },
      {
        label: 'Summarizing',
        value: 'summarizing',
        onClick: () => handleFilterItemClick('status', 'summarizing'),
        count: getStatusCount('summarizing')
      },
      {
        label: 'Analyzing',
        value: 'analyzing',
        onClick: () => handleFilterItemClick('status', 'analyzing'),
        count: getStatusCount('analyzing')
      },
      {
        label: 'Completed',
        value: 'completed',
        onClick: () => handleFilterItemClick('status', 'completed'),
        count: getStatusCount('completed')
      },
      {
        label: 'Error',
        value: 'error',
        onClick: () => handleFilterItemClick('status', 'error'),
        count: getStatusCount('error')
      }
    ];
  }, [calls, handleFilterItemClick]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <AdvanceTableProvider {...table}>
      <div className="mb-2 pt-3">
        <div className="d-flex flex-wrap gap-3">
          <SearchBox
            placeholder="Search logs..."
            onChange={handleSearchInputChange}
          />
          <FilterTab tabItems={tabItems} className="pt-3" />
        </div>
      </div>
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 border-translucent position-relative top-1 pb-3">
        <AdvanceTable
          tableProps={{
            size: 'sm',
            className: 'phoenix-table fs-9'
          }}
          rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
        />
        <AdvanceTableFooter pagination />
      </div>
    </AdvanceTableProvider>
  );
};
